import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {OrdineVenditaGroup} from '../../_models/OrdineVenditaGroup';
import {OrdineVendita} from '../../_models/OrdineVendita';
import {CampoUtenteDef} from '../../_models/CampoUtenteDef';
import {OrganizzazioniService} from '../../_services/organizzazioni.service';
import {LicenzeService} from '../../_services/licenze.service';
import {constants} from '../../_helpers/constants';
import {buildOrdineVenditaGridTemplate} from '../../_helpers/ordine-vendita-grid.helper';

@Component({
  selector: 'app-ordini-vendita-groupbymany',
  templateUrl: './ordini-vendita-groupbymany.component.html',
  styleUrls: ['./ordini-vendita-groupbymany.component.scss'],
  standalone: false
})
export class OrdiniVenditaGroupbymanyComponent implements OnChanges {
  @Input() groups: OrdineVenditaGroup[] = [];
  @Input() visibleColumns: string[] = [];
  // Feature 064 — inoltrate alle righe per il render delle colonne Campi Utente.
  @Input() campiUtenteDefinizioni: CampoUtenteDef[] = [];
  @Input() loadMore = false;
  @Input() loading = false;
  @Output() loadMoreRequested = new EventEmitter<void>();
  @Output() ordineSelected = new EventEmitter<OrdineVendita>();
  @Output() ordineDeleted = new EventEmitter<OrdineVendita>();
  @Output() creaPianificaRequested = new EventEmitter<{ ordini: OrdineVendita[]; group: OrdineVenditaGroup }>();

  expandedGroups = new Set<number>();
  gridTemplate = '';
  protected readonly constants = constants;

  // Chiavi sottogruppo: offset elevato per non collidere con gli indici dei gruppi di
  // primo livello (gi). Senza offset, i sottogruppi del gruppo 0 (gi*1000+si = 0,1,2,...)
  // collidono con gli indici dei gruppi 0,1,2,...
  private static readonly SUBGROUP_OFFSET = 1_000_000;

  subKey(gi: number, si: number): number {
    return OrdiniVenditaGroupbymanyComponent.SUBGROUP_OFFSET + gi * 1000 + si;
  }

  constructor(
    private organizzazioniService: OrganizzazioniService,
    public licenseService: LicenzeService
  ) {}

  // All'apertura della vista raggruppata (e su refresh/filtro/sort) i gruppi arrivano
  // espansi di default. Su "Carica altri gruppi" si conserva lo stato dei gruppi esistenti
  // e si espandono solo quelli nuovi appena aggiunti in coda.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visibleColumns']) {
      this.gridTemplate = buildOrdineVenditaGridTemplate(this.visibleColumns ?? []);
    }
    if (!changes['groups']) return;
    const prevLen = changes['groups'].previousValue?.length ?? 0;
    const currLen = this.groups.length;
    if (currLen < prevLen || prevLen === 0) {
      this.expandedGroups = new Set<number>();
      this.groups.forEach((g, gi) => this.addExpanded(gi, g));
    } else if (currLen > prevLen) {
      for (let gi = prevLen; gi < currLen; gi++) {
        this.addExpanded(gi, this.groups[gi]);
      }
    }
  }

  private addExpanded(gi: number, group: OrdineVenditaGroup): void {
    this.expandedGroups.add(gi);
    (group.subGroups ?? []).forEach((_, si) => this.expandedGroups.add(this.subKey(gi, si)));
  }

  private totalExpandableCount(): number {
    return this.groups.reduce((acc, g) => acc + 1 + (g.subGroups?.length ?? 0), 0);
  }

  get allExpanded(): boolean {
    const total = this.totalExpandableCount();
    return total > 0 && this.expandedGroups.size >= total;
  }

  // Toggle globale: comprime/espande tutti i gruppi e sottogruppi in un colpo solo.
  toggleAll(): void {
    if (this.allExpanded) {
      this.expandedGroups = new Set<number>();
    } else {
      const next = new Set<number>();
      this.groups.forEach((g, gi) => {
        next.add(gi);
        (g.subGroups ?? []).forEach((_, si) => next.add(this.subKey(gi, si)));
      });
      this.expandedGroups = next;
    }
  }

  collectOrders(group: OrdineVenditaGroup): OrdineVendita[] {
    if (group.items?.length) return group.items;
    return (group.subGroups ?? []).reduce<OrdineVendita[]>((acc, sg) => acc.concat(this.collectOrders(sg)), []);
  }

  onCreaPianifica(group: OrdineVenditaGroup, event: Event): void {
    event.stopPropagation();
    const ordini = this.collectOrders(group);
    if (ordini.length > 0) {
      this.creaPianificaRequested.emit({ ordini, group });
    }
  }

  toggleGroup(index: number) {
    if (this.expandedGroups.has(index)) {
      this.expandedGroups.delete(index);
    } else {
      this.expandedGroups.add(index);
    }
  }

  isExpanded(index: number): boolean {
    return this.expandedGroups.has(index);
  }

  trackByOrdineId(_: number, o: OrdineVendita): string {
    return `${o.idOrdineVendita}-${o.idRiga}`;
  }
}
